import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import { SideBar } from '../../layout';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import ResponsiveContainer from '../../layout/Container';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HistoryIcon from '@mui/icons-material/History';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import HomeIcon from '@mui/icons-material/Home';
import HRService from '../../../services/HrService';
import DynamicSnackbar from '../../common/Snackbar';

const HRChangeHistory = () => {
    const navigate = useNavigate();
    const [changes, setChanges] = useState([]);
    const [summary, setSummary] = useState({
        total_changes: 0,
        department_changes: 0,
        job_title_changes: 0,
        recent_changes: 0,
        automated_changes: 0,
        manual_changes: 0
    });
    const [loading, setLoading] = useState(true);
    const [pageCount, setPageCount] = useState(0);
    const [pagination, setPagination] = useState({
        page: 1,
        page_size: 20,
        count: 0
    });
    const [filters, setFilters] = useState({
        user_id: '',
        email: '',
        days: '30',
        change_type: 'both'
    });
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [expandedFilters, setExpandedFilters] = useState(false);

    // Load change history and summary on mount and when filters change
    useEffect(() => {
        fetchChangeHistory();
        fetchSummary();
    }, [pagination.page, pagination.page_size, filters]);

    const fetchChangeHistory = async () => {
        setLoading(true);
        try {
            // Apply active filters
            const activeFilters = {
                page: pagination.page,
                page_size: pagination.page_size
            };
            
            if (filters.user_id) activeFilters.user_id = filters.user_id;
            if (filters.email) activeFilters.email = filters.email;
            if (filters.days && filters.days !== 'all') activeFilters.days = filters.days;
            if (filters.change_type && filters.change_type !== 'both') activeFilters.change_type = filters.change_type;
            
            const result = await HRService.fetchHRChangeHistory(activeFilters);
            setChanges(result.results || []);
            setPagination({
                ...pagination,
                count: result.count || 0
            });
            setPageCount(Math.ceil((result.count || 0) / pagination.page_size));
        } catch (error) {
            console.error('Error fetching change history:', error);
            setSnackbarMessage('Failed to load change history');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async () => {
        try {
            const result = await HRService.fetchHRChangeHistorySummary();
            setSummary(result);
        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    };

    const handlePageChange = (event, value) => {
        setPagination({
            ...pagination,
            page: value
        });
    };

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters({
            ...filters,
            [name]: value
        });
    };

    const handleApplyFilters = () => {
        // Reset to first page when applying new filters
        setPagination({
            ...pagination,
            page: 1
        });
    };

    const handleClearFilters = () => {
        setFilters({
            user_id: '',
            email: '',
            days: '30',
            change_type: 'both'
        });
        // Reset pagination
        setPagination({
            ...pagination,
            page: 1
        });
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    // Render summary statistics cards
    const renderSummaryCards = () => {
        const cards = [
            {
                title: 'Total Changes',
                value: summary.total_changes,
                icon: <CompareArrowsIcon sx={{ fontSize: 40 }} />,
                color: 'primary.main'
            },
            {
                title: 'Department Changes',
                value: summary.department_changes,
                icon: <BusinessIcon sx={{ fontSize: 40 }} />,
                color: 'secondary.main'
            },
            {
                title: 'Job Title Changes',
                value: summary.job_title_changes,
                icon: <WorkIcon sx={{ fontSize: 40 }} />,
                color: 'info.main'
            },
            {
                title: 'Recent Changes (30 days)',
                value: summary.recent_changes,
                icon: <CalendarTodayIcon sx={{ fontSize: 40 }} />,
                color: 'success.main'
            },
            {
                title: 'Automated Updates',
                value: summary.automated_changes,
                icon: <AutorenewIcon sx={{ fontSize: 40 }} />,
                color: 'warning.main'
            },
            {
                title: 'Manual Uploads',
                value: summary.manual_changes,
                icon: <UploadFileIcon sx={{ fontSize: 40 }} />,
                color: 'error.main'
            }
        ];

        return (
            <mui.Grid container spacing={2} sx={{ mb: 4 }}>
                {cards.map((card, index) => (
                    <mui.Grid item xs={6} sm={4} md={2} key={index}>
                        <mui.Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 6
                                }
                            }}
                        >
                            <mui.Box
                                sx={{
                                    color: card.color,
                                    mb: 1
                                }}
                            >
                                {card.icon}
                            </mui.Box>
                            <mui.Typography variant="h4" color={card.color} sx={{ fontWeight: 'bold', mb: 1 }}>
                                {card.value}
                            </mui.Typography>
                            <mui.Typography variant="body2" color="text.secondary" align="center">
                                {card.title}
                            </mui.Typography>
                        </mui.Paper>
                    </mui.Grid>
                ))}
            </mui.Grid>
        );
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Breadcrumbs aria-label="breadcrumb">
                    <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
                        <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                        Home
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/Dashboard">
                        Dashboard
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/hr-data-management">
                        HR Data
                    </mui.Link>
                    <mui.Typography color="text.primary">Change History</mui.Typography>
                </mui.Breadcrumbs>

                <SearchAppBar title="HR Record Change History" icon={<HistoryIcon />} />
                <mui.Typography sx={{ marginTop: '20px' }} variant="subtitle2" gutterBottom>
                    Track changes to employee departments and job titles
                </mui.Typography>

                <Separator />

                {/* Summary Statistics */}
                {renderSummaryCards()}

                {/* Filters */}
                <mui.Paper elevation={3} sx={{ p: 2, mb: 3 }}>
                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <mui.Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <FilterAltIcon sx={{ mr: 1 }} />
                            Filters
                        </mui.Typography>
                        <mui.Button
                            size="small"
                            variant="outlined"
                            onClick={() => setExpandedFilters(!expandedFilters)}
                        >
                            {expandedFilters ? 'Collapse Filters' : 'Expand Filters'}
                        </mui.Button>
                    </mui.Box>

                    <mui.Collapse in={expandedFilters} timeout="auto">
                        <mui.Grid container spacing={2} sx={{ mb: 2 }}>
                            <mui.Grid item xs={12} md={3}>
                                <mui.TextField
                                    fullWidth
                                    label="User ID"
                                    name="user_id"
                                    value={filters.user_id}
                                    onChange={handleFilterChange}
                                    InputProps={{
                                        startAdornment: (
                                            <mui.InputAdornment position="start">
                                                <PersonIcon />
                                            </mui.InputAdornment>
                                        )
                                    }}
                                />
                            </mui.Grid>
                            <mui.Grid item xs={12} md={3}>
                                <mui.TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    value={filters.email}
                                    onChange={handleFilterChange}
                                    InputProps={{
                                        startAdornment: (
                                            <mui.InputAdornment position="start">
                                                <EmailIcon />
                                            </mui.InputAdornment>
                                        )
                                    }}
                                />
                            </mui.Grid>
                            <mui.Grid item xs={12} md={3}>
                                <mui.FormControl fullWidth>
                                    <mui.InputLabel id="time-period-label">Time Period</mui.InputLabel>
                                    <mui.Select
                                        labelId="time-period-label"
                                        name="days"
                                        value={filters.days}
                                        label="Time Period"
                                        onChange={handleFilterChange}
                                    >
                                        <mui.MenuItem value="7">Last 7 days</mui.MenuItem>
                                        <mui.MenuItem value="30">Last 30 days</mui.MenuItem>
                                        <mui.MenuItem value="90">Last 90 days</mui.MenuItem>
                                        <mui.MenuItem value="365">Last year</mui.MenuItem>
                                        <mui.MenuItem value="all">All time</mui.MenuItem>
                                    </mui.Select>
                                </mui.FormControl>
                            </mui.Grid>
                            <mui.Grid item xs={12} md={3}>
                                <mui.FormControl fullWidth>
                                    <mui.InputLabel id="change-type-label">Change Type</mui.InputLabel>
                                    <mui.Select
                                        labelId="change-type-label"
                                        name="change_type"
                                        value={filters.change_type}
                                        label="Change Type"
                                        onChange={handleFilterChange}
                                    >
                                        <mui.MenuItem value="both">All Changes</mui.MenuItem>
                                        <mui.MenuItem value="department">Department Changes</mui.MenuItem>
                                        <mui.MenuItem value="job_title">Job Title Changes</mui.MenuItem>
                                    </mui.Select>
                                </mui.FormControl>
                            </mui.Grid>
                        </mui.Grid>

                        <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <mui.Button variant="outlined" onClick={handleClearFilters}>
                                Clear Filters
                            </mui.Button>
                            <mui.Button variant="contained" onClick={handleApplyFilters}>
                                Apply Filters
                            </mui.Button>
                        </mui.Box>
                    </mui.Collapse>
                </mui.Paper>

                {/* Change History Table */}
                <mui.Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
                    <mui.TableContainer sx={{ maxHeight: 600 }}>
                        <mui.Table stickyHeader>
                            <mui.TableHead>
                                <mui.TableRow>
                                    <mui.TableCell>User ID</mui.TableCell>
                                    <mui.TableCell>Email</mui.TableCell>
                                    <mui.TableCell>Change Date</mui.TableCell>
                                    <mui.TableCell>Department Change</mui.TableCell>
                                    <mui.TableCell>Job Title Change</mui.TableCell>
                                    <mui.TableCell>Change Type</mui.TableCell>
                                </mui.TableRow>
                            </mui.TableHead>
                            <mui.TableBody>
                                {loading ? (
                                    <mui.TableRow>
                                        <mui.TableCell colSpan={6} align="center">
                                            <mui.CircularProgress />
                                        </mui.TableCell>
                                    </mui.TableRow>
                                ) : changes.length === 0 ? (
                                    <mui.TableRow>
                                        <mui.TableCell colSpan={6} align="center">
                                            <mui.Typography variant="body1" color="text.secondary">
                                                No change history records found
                                            </mui.Typography>
                                            <mui.Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                {filters.user_id || filters.email ? 
                                                    "Try adjusting your filters or check different time periods" : 
                                                    "Changes to job titles and departments will appear here when detected"}
                                            </mui.Typography>
                                        </mui.TableCell>
                                    </mui.TableRow>
                                ) : (
                                    changes.map((change) => (
                                        <mui.TableRow key={change.id} hover>
                                            <mui.TableCell>{change.USER_ID}</mui.TableCell>
                                            <mui.TableCell>{change.EMAIL_ADDRESS}</mui.TableCell>
                                            <mui.TableCell>{formatDate(change.CHANGE_DATE)}</mui.TableCell>
                                            <mui.TableCell>
                                                {change.PREVIOUS_DEPARTMENT && change.NEW_DEPARTMENT ? (
                                                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <mui.Typography 
                                                            variant="body2" 
                                                            color="text.secondary"
                                                            sx={{ 
                                                                textDecoration: 'line-through',
                                                                mr: 1
                                                            }}
                                                        >
                                                            {change.PREVIOUS_DEPARTMENT}
                                                        </mui.Typography>
                                                        <ArrowRightIcon sx={{ mx: 0.5, color: 'text.disabled' }} />
                                                        <mui.Typography 
                                                            variant="body2"
                                                            sx={{ 
                                                                fontWeight: 'medium',
                                                                color: 'success.main'
                                                            }}
                                                        >
                                                            {change.NEW_DEPARTMENT}
                                                        </mui.Typography>
                                                    </mui.Box>
                                                ) : (
                                                    <mui.Typography variant="body2" color="text.disabled">
                                                        No change
                                                    </mui.Typography>
                                                )}
                                            </mui.TableCell>
                                            <mui.TableCell>
                                                {change.PREVIOUS_JOB_TITLE && change.NEW_JOB_TITLE ? (
                                                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <mui.Typography 
                                                            variant="body2" 
                                                            color="text.secondary"
                                                            sx={{ 
                                                                textDecoration: 'line-through',
                                                                mr: 1
                                                            }}
                                                        >
                                                            {change.PREVIOUS_JOB_TITLE}
                                                        </mui.Typography>
                                                        <ArrowRightIcon sx={{ mx: 0.5, color: 'text.disabled' }} />
                                                        <mui.Typography 
                                                            variant="body2"
                                                            sx={{ 
                                                                fontWeight: 'medium',
                                                                color: 'info.main'
                                                            }}
                                                        >
                                                            {change.NEW_JOB_TITLE}
                                                        </mui.Typography>
                                                    </mui.Box>
                                                ) : (
                                                    <mui.Typography variant="body2" color="text.disabled">
                                                        No change
                                                    </mui.Typography>
                                                )}
                                            </mui.TableCell>
                                            <mui.TableCell>
                                                <mui.Chip
                                                    label={change.CHANGE_TYPE === 'UPLOAD' ? 'Manual Upload' : 'Automated Job'}
                                                    color={change.CHANGE_TYPE === 'UPLOAD' ? 'error' : 'warning'}
                                                    size="small"
                                                    variant="outlined"
                                                    icon={change.CHANGE_TYPE === 'UPLOAD' ? 
                                                        <UploadFileIcon fontSize="small" /> : 
                                                        <AutorenewIcon fontSize="small" />}
                                                />
                                            </mui.TableCell>
                                        </mui.TableRow>
                                    ))
                                )}
                            </mui.TableBody>
                        </mui.Table>
                    </mui.TableContainer>
                    
                    {/* Pagination */}
                    <mui.Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <mui.Typography variant="body2" color="text.secondary">
                            Showing {changes.length} of {pagination.count} records
                        </mui.Typography>
                        <mui.Pagination
                            count={pageCount}
                            page={pagination.page}
                            onChange={handlePageChange}
                            color="primary"
                            shape="rounded"
                        />
                    </mui.Box>
                </mui.Paper>
            </ResponsiveContainer>

            <DynamicSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={handleSnackbarClose}
            />
        </div>
    );

    return (
        <div>
            <SideBar mainContent={customMainContent} />
        </div>
    );
};

export default HRChangeHistory; 